import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { useProfile } from '@/lib/profile-context';
import { settingsAPI } from '@/lib/api';
import { userCanAccessFeature, reloadAppForPlanAccess, persistBillingToStoredUser, userQualifiesForAuditCredit } from '@/lib/plan-access';
import PlansBillingTab from '@/components/settings/PlansBillingTab';
import AuditPlanHomeCard, { AUDIT_PLAN_INCLUDES } from '@/components/billing/AuditPlanHomeCard';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Store, Mail, CreditCard, ArrowUpRight, CheckCircle2 } from 'lucide-react';

const DIGEST_OPTIONS = [
  { value: 'off', label: 'Off — no scheduled summary' },
  { value: 'daily', label: 'Daily — morning health summary' },
  { value: 'weekly', label: 'Weekly — Monday health summary' },
];

export default function SettingsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, canConnectAmazon, needsSubscription, logout, refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState('');
  const [emailNotif, setEmailNotif] = useState(true);
  const [alertNotif, setAlertNotif] = useState(true);
  const [digestFrequency, setDigestFrequency] = useState('weekly');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [billingData, setBillingData] = useState(null);
  const [billingLoading, setBillingLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const planRequiredToastShown = useRef(false);
  const paymentConfirmStarted = useRef(false);
  const { hasSelection } = useProfile();
  const hasMonitor = userCanAccessFeature(user, 'monitor');
  const hasPoa = userCanAccessFeature(user, 'poa');
  const currentPlan = user?.plan || billingData?.billing?.plan || 'none';

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment');
    const sessionId = params.get('session_id');

    if (paymentStatus !== 'success' || !sessionId || paymentConfirmStarted.current) {
      return;
    }

    paymentConfirmStarted.current = true;
    let active = true;

    const confirm = async () => {
      setConfirmingPayment(true);
      try {
        const confirmRes = await settingsAPI.confirmCheckout(sessionId);
        const billingRes = await settingsAPI.getBilling();
        if (!active) return;

        const billing = billingRes.data?.billing || confirmRes.data?.billing || {};
        const updatedUser = persistBillingToStoredUser(billing);
        await refreshUser();

        setBillingData(billingRes.data);
        setActiveTab('plans');
        toast.success('Payment confirmed — your plan is now active.');
        window.history.replaceState({}, '', `${window.location.pathname}#plans`);

        if (billing.plan_active) {
          reloadAppForPlanAccess(updatedUser, billing);
          return;
        }
      } catch (err) {
        if (!active) return;
        toast.error(err.response?.data?.message || 'Could not confirm payment yet. Try refreshing in a moment.');
      } finally {
        setConfirmingPayment(false);
      }
    };

    confirm();

    return () => {
      active = false;
    };
  }, [refreshUser]);

  const refreshBilling = useCallback(async () => {
    const res = await settingsAPI.getBilling();
    setBillingData(res.data);
    await refreshUser();
    return res.data;
  }, [refreshUser]);

  const reloadForPlanAccess = useCallback(async (billingSnapshot) => {
    await refreshUser();
    let billing = billingSnapshot;
    if (!billing?.plan_features) {
      const data = await refreshBilling();
      billing = data?.billing;
    }
    const storedUser = JSON.parse(localStorage.getItem('protego_user') || 'null') || user;
    reloadAppForPlanAccess(storedUser, billing);
  }, [refreshUser, refreshBilling, user]);

  useEffect(() => {
    if (!location.state?.planRequired || planRequiredToastShown.current) {
      return;
    }
    planRequiredToastShown.current = true;
    toast.message('Live dashboard requires Protego Monitor. Your current plan is listed below.');
    navigate(location.pathname + location.hash, { replace: true, state: {} });
  }, [location, navigate]);

  useEffect(() => {
    if (window.location.hash === '#plans' || needsSubscription) {
      setActiveTab('plans');
    }
  }, [needsSubscription]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await settingsAPI.getProfile();
        setProfile(res.data);
        setEmailNotif(res.data.notifications_email ?? true);
        setAlertNotif(res.data.notifications_alerts ?? true);
        setDigestFrequency(res.data.email_digest_frequency ?? 'weekly');
      } catch (err) {
        console.error('Failed to load profile:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const loadBilling = async () => {
      setBillingLoading(true);
      try {
        const res = await settingsAPI.getBilling();
        setBillingData(res.data);
      } catch (err) {
        console.error('Failed to load billing:', err);
        toast.error('Could not load billing from API — showing default plans.');
      } finally {
        setBillingLoading(false);
      }
    };
    loadBilling();
  }, []);

  const planIncludesContent = () => {
    if (currentPlan === 'audit') {
      return AUDIT_PLAN_INCLUDES;
    }
    if (currentPlan === 'poa') {
      return {
        title: 'Your Plan of Action includes',
        items: [
          'Connect your Amazon account & profile',
          'POA Templates in the product',
          'Customized appeal guidance from your account signals',
        ],
        note: 'Live dashboard monitoring requires Protego Monitor.',
      };
    }
    return null;
  };

  const includes = !hasMonitor && !needsSubscription ? planIncludesContent() : null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.updateProfile({
        notifications_email: emailNotif,
        notifications_alerts: alertNotif,
        email_digest_frequency: digestFrequency,
      });
      toast.success('Settings saved successfully');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async (type, mode = 'preview') => {
    const key = `${type}-${mode}`;
    setTesting(key);
    try {
      const res = await settingsAPI.sendTestEmail({
        type,
        mode,
        frequency: digestFrequency === 'off' ? 'weekly' : digestFrequency,
      });
      toast.success(res.data.message || 'Email sent');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send email');
    } finally {
      setTesting('');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setChangingPassword(true);
    try {
      await settingsAPI.changePassword({
        current_password: currentPassword,
        password: newPassword,
        password_confirmation: confirmPassword,
      });
      toast.success('Password updated');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading || confirmingPayment) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96" />
        {confirmingPayment && (
          <p className="text-sm text-muted-foreground">Confirming your payment…</p>
        )}
      </div>
    );
  }

  if (needsSubscription) {
    return (
      <div className="space-y-6" data-testid="settings-paywall">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold">Choose your plan</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Select a plan to unlock the Protego dashboard. Payment is handled securely via Stripe.
            </p>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={() => logout()}>
            Sign out
          </Button>
        </div>
        <PlansBillingTab
          billingData={billingData}
          loading={billingLoading}
          userEmail={profile?.email || user?.email}
          user={user}
          paywall
          onBillingRefresh={refreshBilling}
          onPlanAccessReload={reloadForPlanAccess}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-xl font-semibold">Settings</h1>
        <p className="text-sm text-muted-foreground">Manage your account preferences</p>
      </div>

      {includes && currentPlan === 'audit' && (
        <AuditPlanHomeCard
          hasSelection={hasSelection}
          onUpgradeClick={() => setActiveTab('plans')}
          showAuditCredit={userQualifiesForAuditCredit(user, billingData?.billing)}
        />
      )}

      {includes && currentPlan !== 'audit' && (
        <Card className="bg-card border-[hsl(188,100%,55%)]/25">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">{includes.title}</CardTitle>
            <CardDescription className="text-xs">{includes.note}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ul className="space-y-2">
              {includes.items.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <CheckCircle2 className="w-4 h-4 text-[hsl(142,72%,45%)] shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button type="button" variant="outline" size="sm" asChild>
                <Link to="/accounts">
                  <Store className="w-4 h-4 mr-2" />
                  Amazon Accounts
                </Link>
              </Button>
              {hasPoa && (
                <Button type="button" variant="outline" size="sm" asChild>
                  <Link to="/poa-templates">
                    POA Templates
                    <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
                  </Link>
                </Button>
              )}
              <Button type="button" variant="secondary" size="sm" onClick={() => setActiveTab('plans')}>
                Upgrade to Monitor
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6" data-testid="settings-tabs">
        <TabsList className="bg-muted/30 h-auto flex flex-wrap justify-start gap-1 p-1">
          <TabsTrigger value="profile" className="text-sm">Profile</TabsTrigger>
          <TabsTrigger value="plans" className="text-sm" data-testid="settings-plans-tab">
            Plans & Billing
          </TabsTrigger>
          <TabsTrigger value="security" className="text-sm">Security</TabsTrigger>
          <TabsTrigger value="notifications" className="text-sm">Notifications</TabsTrigger>
          {canConnectAmazon && (
            <TabsTrigger value="connections" className="text-sm">Connections</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="profile">
          <Card className="bg-card border-border/80">
            <CardHeader>
              <CardTitle className="text-base font-semibold">Profile Information</CardTitle>
              <CardDescription className="text-xs">Your account details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm">Name</Label>
                  <Input value={profile?.name || ''} readOnly className="bg-background/40 border-border h-10" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Email</Label>
                  <Input value={profile?.email || ''} readOnly className="bg-background/40 border-border h-10" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Role</Label>
                  <Input value={profile?.role || ''} readOnly className="bg-background/40 border-border h-10" />
                </div>
              </div>

              {(profile?.billing || billingData?.billing) && (
                <div className="rounded-lg border border-border/60 bg-muted/20 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <CreditCard className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium">
                        Plan: {(profile?.billing || billingData?.billing)?.plan_label || 'Free'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {(profile?.billing || billingData?.billing)?.plan_active
                          ? 'Your subscription is active. Payment history is under Plans & Billing.'
                          : 'No active plan — upgrade to unlock dashboard monitoring.'}
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    onClick={() => setActiveTab('plans')}
                  >
                    Plans & billing
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security">
          <Card className="bg-card border-border/80">
            <CardHeader>
              <CardTitle className="text-base font-semibold">Change Password</CardTitle>
              <CardDescription className="text-xs">Update your login password</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                <div className="space-y-2">
                  <Label htmlFor="current_password">Current password</Label>
                  <Input
                    id="current_password"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    className="h-10"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new_password">New password</Label>
                  <Input
                    id="new_password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    className="h-10"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm_password">Confirm new password</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    className="h-10"
                  />
                </div>
                <Button type="submit" disabled={changingPassword} className="grad-accent text-white border-0">
                  {changingPassword ? 'Updating…' : 'Update password'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications">
          <Card className="bg-card border-border/80">
            <CardHeader>
              <CardTitle className="text-base font-semibold">Email & Alerts</CardTitle>
              <CardDescription className="text-xs">
                Scheduled summaries plus instant alerts when your score drops or a KPI enters caution/at-risk
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium">Email summaries</p>
                  <p className="text-xs text-muted-foreground">Receive scheduled health score digests with metrics and trends</p>
                </div>
                <Switch checked={emailNotif} onCheckedChange={setEmailNotif} />
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Summary frequency</Label>
                <Select value={digestFrequency} onValueChange={setDigestFrequency} disabled={!emailNotif}>
                  <SelectTrigger className="bg-background/40 border-border h-10">
                    <SelectValue placeholder="Choose frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    {DIGEST_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium">Instant alerts</p>
                  <p className="text-xs text-muted-foreground">Score drops and individual KPI zone changes (caution / at risk)</p>
                </div>
                <Switch checked={alertNotif} onCheckedChange={setAlertNotif} />
              </div>

              <div className="rounded-lg border border-border/60 bg-muted/20 p-4 space-y-3">
                <div>
                  <p className="text-sm font-medium">Try before go-live</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Preview emails include a banner and &quot;(Preview)&quot; in the subject — only for testing.
                    Live scheduled and alert emails use the same template without that banner.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!!testing}
                    onClick={() => handleTestEmail('digest', 'preview')}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    {testing === 'digest-preview' ? 'Sending…' : 'Preview summary'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!!testing}
                    onClick={() => handleTestEmail('alert', 'preview')}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    {testing === 'alert-preview' ? 'Sending…' : 'Preview score alert'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!!testing}
                    onClick={() => handleTestEmail('kpi', 'preview')}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    {testing === 'kpi-preview' ? 'Sending…' : 'Preview KPI alert'}
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 pt-1 border-t border-border/40">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={!!testing}
                    onClick={() => handleTestEmail('digest', 'live')}
                  >
                    {testing === 'digest-live' ? 'Sending…' : 'Send live-format summary'}
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Use &quot;live-format&quot; once before launch to confirm the exact email customers receive.
                </p>
              </div>

              <Button onClick={handleSave} disabled={saving} data-testid="settings-save-button" className="grad-accent text-white border-0 hover:opacity-90 transition-opacity">
                {saving ? 'Saving...' : 'Save Preferences'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="plans">
          <PlansBillingTab
            billingData={billingData}
            loading={billingLoading}
            userEmail={profile?.email || user?.email}
            user={user}
            onBillingRefresh={refreshBilling}
            onPlanAccessReload={reloadForPlanAccess}
          />
        </TabsContent>

        {canConnectAmazon && (
        <TabsContent value="connections">
          <Card className="bg-card border-border/80">
            <CardHeader>
              <CardTitle className="text-base font-semibold">Connected Stores</CardTitle>
              <CardDescription className="text-xs">Manage your Amazon store connections</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="p-8 border border-dashed border-border/80 rounded-lg text-center">
                <Store className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
                <h3 className="text-sm font-medium mb-2">Connect Amazon Store</h3>
                <p className="text-xs text-muted-foreground mb-4 max-w-sm mx-auto">
                  SP-API integration coming soon. Your data will sync automatically once connected.
                </p>
                <Button variant="outline" className="border-border/60 text-muted-foreground" disabled>
                  Connect Amazon Store
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
